import type { TranslateFn } from "@/lib/i18n/intl";
import type { RestaurantNotification } from "@/lib/firebase/notification-feed";
import { RESTAURANT_NOTIFICATION_TYPES } from "@/lib/firebase/notification-types";
import { countedKey } from "@/lib/i18n/plural";
import type {
  RestaurantApprovalNotificationData,
  RestaurantInvoiceNotificationData,
  RestaurantOnlineOrderNotificationData,
} from "./notificationBehavior";

export { RESTAURANT_NOTIFICATION_TYPES } from "@/lib/firebase/notification-types";

export function restaurantNotificationCopy(
  notification: Pick<RestaurantNotification, "title" | "body" | "type"> & {
    /** The day-closed push carries no counts; its copy is the server's own. */
    data?:
      | Partial<RestaurantInvoiceNotificationData>
      | { businessDate?: string }
      | Partial<RestaurantApprovalNotificationData>
      | Partial<RestaurantOnlineOrderNotificationData>;
  },
  t: TranslateFn,
): { title: string; body: string } {
  const data = notification.data as
    | Partial<RestaurantInvoiceNotificationData>
    | undefined;
  switch (notification.type) {
    case RESTAURANT_NOTIFICATION_TYPES.itemsRequested: {
      const count = data?.itemCount;
      return {
        title: t((messages) => messages.notifications.itemsRequestedTitle),
        body:
          count === undefined
            ? t((messages) => messages.notifications.itemsRequestedBodyFallback)
            : t(
                countedKey(count, {
                  one: (messages) => messages.notifications.itemsRequestedBodyOne,
                  two: (messages) => messages.notifications.itemsRequestedBodyTwo,
                  few: (messages) => messages.notifications.itemsRequestedBodyFew,
                  many: (messages) => messages.notifications.itemsRequestedBody,
                }),
                { count },
              ),
      };
    }
    // 2026-10-20 — titled by its channel (without one, the server's own
    // title); the plate count words the body. Never the customer's name: the
    // row and the push carry none.
    case RESTAURANT_NOTIFICATION_TYPES.onlineOrderRequested: {
      const online = notification.data as
        | Partial<RestaurantOnlineOrderNotificationData>
        | undefined;
      const count = online?.itemCount;
      return {
        title:
          online?.channel === "DELIVERY"
            ? t((messages) => messages.notifications.onlineDeliveryRequestedTitle)
            : online?.channel === "TAKEAWAY"
              ? t((messages) => messages.notifications.onlineTakeawayRequestedTitle)
              : notification.title ||
                t((messages) => messages.notifications.onlineTakeawayRequestedTitle),
        body:
          count === undefined
            ? t((messages) => messages.notifications.onlineOrderRequestedBodyFallback)
            : t(
                countedKey(count, {
                  one: (messages) => messages.notifications.onlineOrderRequestedBodyOne,
                  two: (messages) => messages.notifications.onlineOrderRequestedBodyTwo,
                  few: (messages) => messages.notifications.onlineOrderRequestedBodyFew,
                  many: (messages) => messages.notifications.onlineOrderRequestedBody,
                }),
                { count },
              ),
      };
    }
    case RESTAURANT_NOTIFICATION_TYPES.paymentSubmitted:
      return {
        title: t((messages) => messages.notifications.paymentSubmittedTitle),
        body: t((messages) => messages.notifications.paymentSubmittedBody),
      };
    case RESTAURANT_NOTIFICATION_TYPES.kitchenTicketsCreated: {
      const count = data?.ticketCount;
      return {
        title: t((messages) => messages.notifications.kitchenTicketsTitle),
        body:
          count === undefined
            ? t((messages) => messages.notifications.kitchenTicketsBodyFallback)
            : t(
                countedKey(count, {
                  one: (messages) => messages.notifications.kitchenTicketsBodyOne,
                  two: (messages) => messages.notifications.kitchenTicketsBodyTwo,
                  few: (messages) => messages.notifications.kitchenTicketsBodyFew,
                  many: (messages) => messages.notifications.kitchenTicketsBody,
                }),
                { count },
              ),
      };
    }
    case RESTAURANT_NOTIFICATION_TYPES.itemsReady: {
      const count = data?.itemCount;
      return {
        title: t((messages) => messages.notifications.itemsReadyTitle),
        body:
          count === undefined
            ? t((messages) => messages.notifications.itemsReadyBodyFallback)
            : t(
                countedKey(count, {
                  one: (messages) => messages.notifications.itemsReadyBodyOne,
                  two: (messages) => messages.notifications.itemsReadyBodyTwo,
                  few: (messages) => messages.notifications.itemsReadyBodyFew,
                  many: (messages) => messages.notifications.itemsReadyBody,
                }),
                { count },
              ),
      };
    }
    // D156 — the approver's notice, re-worded in the reader's language from
    // the facts the event carries; without them, the server's own words.
    case RESTAURANT_NOTIFICATION_TYPES.approvalUsed: {
      const approval = notification.data as
        | Partial<RestaurantApprovalNotificationData>
        | undefined;
      const act = approval?.commandType;
      if (!act || approval?.dailySequence === undefined || !approval.localTime) {
        return { title: notification.title, body: notification.body };
      }
      const values = {
        act: t((messages) => messages.managerApproval[`act_${act}`]),
        number: String(approval.dailySequence),
        time: approval.localTime,
        till: approval.tillName ?? "",
      };
      return {
        title: t((messages) => messages.managerApproval.notifyTitle),
        body: approval.tillName
          ? t((messages) => messages.managerApproval.notifyBodyTill, values)
          : t((messages) => messages.managerApproval.notifyBody, values),
      };
    }
    // D151 — composed in Arabic by the API from the Z it read, figures and
    // all; there is nothing for the client to count or re-word.
    case RESTAURANT_NOTIFICATION_TYPES.dayClosed:
    default:
      return { title: notification.title, body: notification.body };
  }
}

export function formatNotificationRelativeTime(
  date: Date,
  t: TranslateFn,
  now = new Date(),
): string {
  const elapsedSeconds = Math.max(
    0,
    Math.floor((now.getTime() - date.getTime()) / 1000),
  );
  if (elapsedSeconds < 60) return t((messages) => messages.notifications.timeNow);
  const elapsedMinutes = Math.floor(elapsedSeconds / 60);
  if (elapsedMinutes < 60) {
    return t((messages) => messages.notifications.timeMinutesAgo, {
      count: elapsedMinutes,
    });
  }
  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 24) {
    return t((messages) => messages.notifications.timeHoursAgo, {
      count: elapsedHours,
    });
  }
  return t((messages) => messages.notifications.timeDaysAgo, {
    count: Math.floor(elapsedHours / 24),
  });
}
